import { EdgerRollPass } from "@/pages/catalogs/models/EdgerRoll";
import {
	getPassColor,
	PassCircle,
} from "@/pages/catalogs/pages/edger-rolls/components/passes/PassesGrid";
import {
	EdgerPassSelector,
	isPassDisabled,
} from "@/shared/selectors/EdgerPassSelector";
import { backgroundColor } from "@/utils/theme";
import {
	ButtonWithLoading,
	getMomentTz,
	TimeService,
} from "@dexteel/mesf-core";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Collapse,
	Grid2,
	IconButton,
	Popover,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
	useTheme,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { CarrierForm } from "../../models/CarrierForm";
import { PassHistoryEntry } from "../../models/Passes";
import {
	deleteCarrierInstancePass,
	getCarrierInstancePassHistory,
	upsertCarrierInstancePass,
} from "../../repositories/CarrierAssemblyRepository";

const moment = getMomentTz();

export const usePassHistory = (
	carrierInstanceId: number | null,
	onError: (error: Error) => void,
	enabled: boolean,
) => {
	return useQuery<PassHistoryEntry[], Error>({
		queryKey: ["passHistory", carrierInstanceId],
		queryFn: ({ signal }) =>
			getCarrierInstancePassHistory(
				{ carrierInstanceId: carrierInstanceId! },
				signal,
			),
		enabled: enabled && !!carrierInstanceId,
		onError,
	});
};

interface HistoryTableProps {
	entries: PassHistoryEntry[];
	editingEntry: PassHistoryEntry | null;
	onEditEntry: (
		event: React.MouseEvent<HTMLElement>,
		entry: PassHistoryEntry,
	) => void;
	onDeleteEntry: (carrierInstancePassId: number) => void;
	isDeleting: boolean;
	installedAt: Date | null;
	uninstalledAt: Date | null;
}

const HistoryTable = ({
	entries,
	editingEntry,
	onEditEntry,
	onDeleteEntry,
	isDeleting,
	installedAt,
	uninstalledAt,
}: HistoryTableProps) => {
	const theme = useTheme();

	const timelineRange = useMemo(() => {
		if (!installedAt) return null;
		const start = installedAt.getTime();
		const end = (uninstalledAt ?? new Date()).getTime();
		return { start, end, duration: end - start };
	}, [installedAt, uninstalledAt]);

	const getTimelineBar = (entry: PassHistoryEntry, index: number) => {
		if (!timelineRange || timelineRange.duration <= 0) return null;

		const entryStart = entry.StartedAt.getTime();
		const entryEnd =
			entry.EndedAt?.getTime() ??
			entries[index + 1]?.StartedAt.getTime() ??
			(uninstalledAt ?? new Date()).getTime();

		const left =
			((entryStart - timelineRange.start) / timelineRange.duration) * 100;
		const width = ((entryEnd - entryStart) / timelineRange.duration) * 100;

		return {
			left: Math.max(0, Math.min(left, 100)),
			width: Math.max(0.5, Math.min(width, 100 - left)),
		};
	};

	if (entries.length === 0) {
		return (
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					height: 100,
					border: 1,
					borderColor: "divider",
					borderRadius: 1,
					color: "text.secondary",
				}}
			>
				<Typography variant="body2">No pass history</Typography>
			</Box>
		);
	}

	return (
		<TableContainer
			sx={{
				maxHeight: 300,
				border: 1,
				borderColor: "divider",
				borderRadius: 1,
			}}
		>
			<Table size="small" stickyHeader>
				<TableHead>
					<TableRow sx={{ "& th": { py: 1, fontSize: 12 } }}>
						<TableCell sx={{ fontWeight: 600 }}>Pass</TableCell>
						<TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
						<TableCell sx={{ fontWeight: 600 }}>Started</TableCell>
						<TableCell sx={{ fontWeight: 600 }}>Ended</TableCell>
						<TableCell sx={{ fontWeight: 600, minWidth: 120 }}>
							Timeline
						</TableCell>
						<TableCell sx={{ fontWeight: 600, width: 80 }} />
					</TableRow>
				</TableHead>
				<TableBody>
					{entries.map((entry, index) => {
						const isEditing =
							editingEntry?.CarrierInstancePassId ===
							entry.CarrierInstancePassId;
						const bar = getTimelineBar(entry, index);

						return (
							<TableRow
								key={entry.CarrierInstancePassId}
								data-test-id={`pass-history-row-${entry.CarrierInstancePassId}`}
								sx={{
									bgcolor: isEditing
										? "action.selected"
										: entry.IsFirst
											? backgroundColor
											: undefined,
									"& td": { py: 0.5, fontSize: 12 },
								}}
							>
								<TableCell data-test-id="pass-number-cell">
									Pass {entry.PassNumber}
								</TableCell>
								<TableCell>
									<Box
										sx={{
											display: "flex",
											alignItems: "center",
											gap: 0.5,
										}}
									>
										<PassCircle
											sx={{ fontSize: 18 }}
											firstColor={
												getPassColor(
													entry.PassStatusArray?.[0] ?? entry.PassStatusCode,
													theme,
												).main
											}
											secondColor={
												entry.PassStatusArray?.[1]
													? getPassColor(entry.PassStatusArray[1], theme).main
													: undefined
											}
										/>
									</Box>
								</TableCell>
								<TableCell>
									{entry.StartedAt
										? moment(entry.StartedAt).format("MM/DD/YYYY HH:mm")
										: "-"}
								</TableCell>
								<TableCell>
									{entry.EndedAt
										? moment(entry.EndedAt).format("MM/DD/YYYY HH:mm")
										: "-"}
								</TableCell>
								<TableCell sx={{ p: 0.5 }}>
									{bar && (
										<Box
											sx={{
												position: "relative",
												height: 16,
												bgcolor: (theme) =>
													theme.palette.mode === "dark"
														? "grey.800"
														: "grey.200",
												borderRadius: 0.5,
												overflow: "hidden",
											}}
										>
											<Box
												sx={{
													position: "absolute",
													top: 2,
													bottom: 2,
													left: `${bar.left}%`,
													width: `${bar.width}%`,
													bgcolor: getPassColor(
														entry.PassStatusArray?.[0] ?? entry.PassStatusCode,
														theme,
													).main,
													borderRadius: 0.5,
													minWidth: 4,
												}}
											/>
										</Box>
									)}
								</TableCell>
								<TableCell sx={{ p: 0 }}>
									<Box sx={{ display: "flex", gap: 0.5 }}>
										<Tooltip title="Edit">
											<IconButton
												size="small"
												onClick={(e) => onEditEntry(e, entry)}
												disabled={isEditing}
												data-test-id="edit-pass-entry-button"
											>
												<EditIcon sx={{ fontSize: 18 }} />
											</IconButton>
										</Tooltip>
										{!entry.IsFirst && (
											<Tooltip title="Delete">
												<IconButton
													size="small"
													onClick={() =>
														onDeleteEntry(entry.CarrierInstancePassId)
													}
													disabled={isDeleting}
													data-test-id="delete-pass-entry-button"
												>
													<DeleteIcon sx={{ fontSize: 18 }} color="error" />
												</IconButton>
											</Tooltip>
										)}
									</Box>
								</TableCell>
							</TableRow>
						);
					})}
				</TableBody>
			</Table>
		</TableContainer>
	);
};

export const PassChangesHistoryTable = ({
	open,
	carrier,
	rollsPasses,
	carrierForm,
	initialTimestamp,
	setError,
	onSaved,
}: {
	open: boolean;
	carrier: { partId: number; carrierInstanceId: number };
	rollsPasses: { top: EdgerRollPass[] | null; bottom: EdgerRollPass[] | null };
	carrierForm: CarrierForm | null;
	initialTimestamp?: Date;
	setError: (error: string) => void;
	onSaved: () => void;
}) => {
	const queryClient = useQueryClient();

	const [showNewPanel, setShowNewPanel] = useState(false);
	const [newTimestamp, setNewTimestamp] = useState<Date>(new Date());
	const [newPassNumber, setNewPassNumber] = useState<number | null>(null);

	const [editingEntry, setEditingEntry] = useState<PassHistoryEntry | null>(
		null,
	);
	const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);

	const { data: passHistory, isLoading: historyLoading } = usePassHistory(
		carrier?.carrierInstanceId ?? null,
		(error) => setError(error.message),
		open,
	);

	const sortedPassHistory = useMemo(() => {
		if (!passHistory) return [];

		const grouped = passHistory.reduce(
			(acc, entry) => {
				const key = `${entry.StartedAt.getTime()}`;

				if (!acc[key]) {
					acc[key] = {
						...entry,
						PassStatusArray: [entry.PassStatusCode],
					};
				} else {
					acc[key].PassStatusArray = [
						acc[key].PassStatusArray![0],
						entry.PassStatusCode,
					];
				}

				return acc;
			},
			{} as Record<string, PassHistoryEntry>,
		);

		return Object.values(grouped).sort(
			(a, b) => a.StartedAt.getTime() - b.StartedAt.getTime(),
		);
	}, [passHistory]);

	const findPassAtTimestamp = useCallback(
		(timestamp: Date): number | null => {
			if (!sortedPassHistory.length) return null;

			const time = timestamp.getTime();

			for (let i = 0; i < sortedPassHistory.length; i++) {
				const entry = sortedPassHistory[i];
				const entryStart = entry.StartedAt.getTime();
				const entryEnd =
					entry.EndedAt?.getTime() ??
					sortedPassHistory[i + 1]?.StartedAt.getTime() ??
					(carrierForm?.UninstalledAt ?? new Date()).getTime();

				if (time >= entryStart && time < entryEnd) {
					return entry.PassNumber;
				}
			}

			return null;
		},
		[sortedPassHistory, carrierForm?.UninstalledAt],
	);

	const passAtCurrentTimestamp = useMemo(() => {
		if (!newTimestamp) return null;
		return findPassAtTimestamp(newTimestamp);
	}, [newTimestamp]);

	const createPassEntry = useMutation(
		async () => {
			if (!newPassNumber) throw new Error("Pass number is required");

			return upsertCarrierInstancePass({
				carrierInstanceId: carrier.carrierInstanceId,
				passNumber: newPassNumber,
				startedAt: newTimestamp,
			});
		},
		{
			meta: { successMessage: "Pass entry created successfully" },
			onSuccess: async () => {
				await queryClient.invalidateQueries({
					queryKey: ["passHistory", carrier?.carrierInstanceId],
				});
				await queryClient.invalidateQueries({ queryKey: ["carriersAssembly"] });
				await queryClient.invalidateQueries({
					queryKey: ["carrierForm", carrier?.partId],
				});
				setShowNewPanel(false);
				setNewPassNumber(null);
				setNewTimestamp(new Date());
				onSaved();
			},
			onError: (error: Error) => setError(error.message),
		},
	);

	const updateHistoryEntry = useMutation(
		async (params: { entry: PassHistoryEntry; newPassNumber: number }) => {
			return upsertCarrierInstancePass({
				carrierInstancePassId: params.entry.CarrierInstancePassId,
				carrierInstanceId: params.entry.CarrierInstanceId,
				passNumber: params.newPassNumber,
				startedAt: params.entry.StartedAt,
			});
		},
		{
			meta: { successMessage: "Pass updated successfully" },
			onSuccess: async () => {
				await queryClient.invalidateQueries({
					queryKey: ["passHistory", carrier?.carrierInstanceId],
				});
				await queryClient.invalidateQueries({ queryKey: ["carriersAssembly"] });
				await queryClient.invalidateQueries({
					queryKey: ["carrierForm", carrier?.partId],
				});
				setPopoverAnchor(null);
				setEditingEntry(null);
				onSaved();
			},
			onError: (error: Error) => setError(error.message),
		},
	);

	const deleteHistoryEntry = useMutation(
		async (carrierInstancePassId: number) => {
			return deleteCarrierInstancePass(carrierInstancePassId);
		},
		{
			meta: { successMessage: "Pass entry deleted successfully" },
			onSuccess: async () => {
				await queryClient.invalidateQueries({
					queryKey: ["passHistory", carrier?.carrierInstanceId],
				});
				await queryClient.invalidateQueries({ queryKey: ["carriersAssembly"] });
				onSaved();
			},
			onError: (error: Error) => setError(error.message),
		},
	);

	const handleEditEntry = (
		event: React.MouseEvent<HTMLElement>,
		entry: PassHistoryEntry,
	) => {
		setPopoverAnchor(event.currentTarget);
		setEditingEntry(entry);
	};

	const handleCancelEdit = () => {
		setPopoverAnchor(null);
		setEditingEntry(null);
	};

	const handleSaveEdit = (newPassNumber: number) => {
		if (!editingEntry) return;
		updateHistoryEntry.mutate({
			entry: editingEntry,
			newPassNumber: newPassNumber,
		});
	};

	const handleDeleteEntry = (carrierInstancePassId: number) => {
		deleteHistoryEntry.mutate(carrierInstancePassId);
	};

	const handleNewClick = () => {
		setShowNewPanel(true);
		setNewTimestamp(initialTimestamp ?? new Date());
		setNewPassNumber(null);
	};

	const handleCancelNew = () => {
		setShowNewPanel(false);
		setNewPassNumber(null);
	};

	const allPassesDisabled = (
		passes: { top: EdgerRollPass[] | null; bottom: EdgerRollPass[] | null },
		sectionName: string | null,
	): boolean => {
		if (!passes.top?.length && !passes.bottom?.length) return true;

		const maxPassNumber = Math.max(
			...(passes.top?.map((p) => p.PassNumber) ?? [0]),
			...(passes.bottom?.map((p) => p.PassNumber) ?? [0]),
		);

		const passNumbers = Array.from({ length: maxPassNumber }, (_, i) => i + 1);

		return passNumbers.every((n) => isPassDisabled(n, passes, sectionName));
	};

	const hasAllPassesDisabled = allPassesDisabled(
		rollsPasses,
		carrierForm?.SectionName ?? null,
	);

	useEffect(() => {
		if (showNewPanel && passAtCurrentTimestamp !== null) {
			setNewPassNumber(passAtCurrentTimestamp);
		} else {
			setNewPassNumber(findPassAtTimestamp(newTimestamp));
		}
	}, [showNewPanel]);

	if (historyLoading) {
		return (
			<Grid2 size={{ xs: 12 }}>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						height: 150,
					}}
				>
					<CircularProgress size={24} />
				</Box>
			</Grid2>
		);
	}

	return (
		<>
			<Grid2 size={{ xs: 12 }}>
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						mb: 1,
					}}
				>
					<Typography variant="subtitle2" fontWeight={600}>
						Pass History
					</Typography>
				</Box>

				<HistoryTable
					entries={sortedPassHistory}
					editingEntry={editingEntry}
					onEditEntry={handleEditEntry}
					onDeleteEntry={handleDeleteEntry}
					isDeleting={deleteHistoryEntry.isPending}
					installedAt={carrierForm?.InstalledAt ?? null}
					uninstalledAt={carrierForm?.UninstalledAt ?? null}
				/>

				{!showNewPanel && (
					<Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
						<Button
							variant="contained"
							color="primary"
							size="small"
							startIcon={<AddIcon />}
							onClick={handleNewClick}
							data-test-id="new-pass-change-button"
						>
							New Pass Change
						</Button>
					</Box>
				)}

				<Collapse in={showNewPanel}>
					<Box
						sx={{
							p: 2,
							mt: 2,
							border: 1,
							borderColor: hasAllPassesDisabled
								? "warning.main"
								: "primary.main",
							borderRadius: 1,
							bgcolor: "background.paper",
						}}
					>
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								mb: 2,
							}}
						>
							<Typography variant="subtitle2" fontWeight={600}>
								New Pass Change
							</Typography>
							<IconButton size="small" onClick={handleCancelNew}>
								<CloseIcon fontSize="small" />
							</IconButton>
						</Box>

						<Grid2 container spacing={2}>
							<Grid2 size={{ xs: 12 }}>
								<DateTimePicker
									label="Timestamp"
									value={moment(newTimestamp)}
									onChange={(date) =>
										setNewTimestamp(date?.toDate() ?? new Date())
									}
									timezone={TimeService.getInstance().getServerTimeZone()}
									format="MM/DD/YYYY HH:mm:ss"
									ampm={false}
									minDateTime={
										carrierForm?.InstalledAt
											? moment(carrierForm.InstalledAt)
											: undefined
									}
									maxDateTime={
										carrierForm?.UninstalledAt
											? moment(carrierForm.UninstalledAt)
											: moment()
									}
									slotProps={{
										textField: {
											size: "small",
											fullWidth: true,
										},
									}}
								/>
							</Grid2>

							<Grid2 size={{ xs: 12 }}>
								<EdgerPassSelector
									value={newPassNumber}
									onChange={setNewPassNumber}
									passes={rollsPasses}
									rollNames={[
										carrierForm?.TopRollName,
										carrierForm?.BottomRollName,
									]}
									rollIds={[carrierForm?.TopRollId, carrierForm?.BottomRollId]}
									sectionName={carrierForm?.SectionName ?? null}
									horizontal
									withShortcut
								/>
							</Grid2>

							<Grid2 size={{ xs: 12 }}>
								{hasAllPassesDisabled && (
									<Alert severity="warning" sx={{ mb: 1 }}>
										No passes are available for selection. Hover each pass to
										see why it's disabled.
									</Alert>
								)}
								<Box
									sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}
								>
									<Button
										variant="outlined"
										color="inherit"
										size="small"
										onClick={handleCancelNew}
										data-test-id="cancel-pass-change-button"
									>
										Cancel
									</Button>
									<ButtonWithLoading
										variant="contained"
										color="primary"
										size="small"
										onClick={() => createPassEntry.mutate()}
										isLoading={createPassEntry.isPending}
										disabled={
											hasAllPassesDisabled ||
											!newPassNumber ||
											newPassNumber === passAtCurrentTimestamp
										}
										data-test-id="save-pass-change-button"
									>
										Save
									</ButtonWithLoading>
								</Box>
							</Grid2>
						</Grid2>
					</Box>
				</Collapse>
			</Grid2>

			<Popover
				open={Boolean(popoverAnchor)}
				anchorEl={popoverAnchor}
				onClose={handleCancelEdit}
				anchorOrigin={{
					vertical: "center",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "center",
					horizontal: "left",
				}}
			>
				{editingEntry && (
					<Box sx={{ p: 2, minWidth: 280 }} data-test-id="edit-pass-popover">
						<Typography variant="subtitle2" gutterBottom>
							Edit Pass
						</Typography>
						<Typography
							variant="caption"
							color="textSecondary"
							sx={{ display: "block", mb: 1 }}
						>
							Started:{" "}
							{moment(editingEntry.StartedAt).format("MM/DD/YYYY HH:mm")}
						</Typography>
						<EdgerPassSelector
							value={editingEntry.PassNumber}
							onChange={(value) => {
								if (value !== null) {
									handleSaveEdit(value);
								}
							}}
							passes={rollsPasses}
							rollNames={[
								carrierForm?.TopRollName,
								carrierForm?.BottomRollName,
							]}
							rollIds={[carrierForm?.TopRollId, carrierForm?.BottomRollId]}
							sectionName={carrierForm?.SectionName ?? null}
						/>
						{updateHistoryEntry.isPending && (
							<Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
								<CircularProgress size={16} sx={{ mr: 1 }} />
								<Typography variant="caption">Saving...</Typography>
							</Box>
						)}
					</Box>
				)}
			</Popover>
		</>
	);
};
